// Notifications settings — placeholder M3 (admin + sellers + vendors).
//
// Volkan direction 2026-05-23 (Settings 10-section expansion item #8):
// Per-channel toggle preview (email / in-app / Slack-style external).
// Backend persistence M3 Stage 4 SPEC.md Faz 5 (notifications engine ratify).

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Bell, Mail, MessageSquare } from 'lucide-react'
import { useT } from '../../i18n'
import { notificationsI18n } from '../../i18n/settings/notifications'
import { apiUrl, USE_REAL_API } from '../../lib/api'
import { useToastStore } from '../../lib/toast'
import type { SettingsSurface } from './Settings'
import { DataTableShell } from '../../components/ui/data-table-shell'

type Channel = 'email' | 'in-app' | 'slack'

type CategoryKey =
  | 'platformIncidents'
  | 'approvalQueue'
  | 'walletSettlement'
  | 'orderEvents'
  | 'walletEvents'
  | 'integrationHealth'
  | 'workOrderEvents'
  | 'capacityEvents'

type CategoryConfig = {
  id: CategoryKey
  defaults: Record<Channel, boolean>
}

const SURFACE_CATEGORY_CONFIG: Record<SettingsSurface, CategoryConfig[]> = {
  admin: [
    {
      id: 'platformIncidents',
      defaults: { email: true, 'in-app': true, slack: true },
    },
    {
      id: 'approvalQueue',
      defaults: { email: false, 'in-app': true, slack: false },
    },
    {
      id: 'walletSettlement',
      defaults: { email: true, 'in-app': true, slack: false },
    },
  ],
  seller: [
    {
      id: 'orderEvents',
      defaults: { email: true, 'in-app': true, slack: false },
    },
    {
      id: 'walletEvents',
      defaults: { email: true, 'in-app': true, slack: false },
    },
    {
      id: 'integrationHealth',
      defaults: { email: false, 'in-app': true, slack: false },
    },
  ],
  supply: [
    {
      id: 'workOrderEvents',
      defaults: { email: true, 'in-app': true, slack: false },
    },
    {
      id: 'capacityEvents',
      defaults: { email: true, 'in-app': true, slack: false },
    },
  ],
}

const CHANNEL_ICONS: Record<Channel, typeof Bell> = {
  email: Mail,
  'in-app': Bell,
  slack: MessageSquare,
}

const CHANNEL_ORDER: Channel[] = ['email', 'in-app', 'slack']

type ApiSubscription = {
  event_type: string
  email_enabled: boolean
  in_app_enabled: boolean
}

// Real-engine: per-event_type email/in-app preference toggles over the backend
// notif_subscriptions table. The FE category key IS the event_type (the backend
// stores it generically). Slack is dropped in real mode — the backend supports
// email/in-app channels only (A-98: NO webhook V1).
function RealNotifications({ surface }: { surface: SettingsSurface }) {
  const push = useToastStore((s) => s.push)
  const t = useT(notificationsI18n)
  const qc = useQueryClient()
  const categories = SURFACE_CATEGORY_CONFIG[surface]

  const subs = useQuery<{ items: ApiSubscription[] }>({
    queryKey: ['notif-subscriptions'],
    queryFn: async () => {
      const res = await fetch(apiUrl('/notification-subscriptions'))
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return res.json()
    },
  })

  const upsert = useMutation({
    mutationFn: async (vars: { eventType: string; email: boolean; inApp: boolean }) => {
      const res = await fetch(apiUrl(`/notification-subscriptions/${vars.eventType}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email_enabled: vars.email, in_app_enabled: vars.inApp }),
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return res.json()
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['notif-subscriptions'] }),
    onError: () =>
      push({ variant: 'error', title: t.real.saveFailed, description: t.real.keepOneChannel, duration_ms: 3500 }),
  })

  const stored = new Map(subs.data?.items.map((s) => [s.event_type, s]) ?? [])
  const stateFor = (cat: CategoryConfig): { email: boolean; inApp: boolean } => {
    const row = stored.get(cat.id)
    if (row) return { email: row.email_enabled, inApp: row.in_app_enabled }
    return { email: cat.defaults.email, inApp: cat.defaults['in-app'] }
  }

  return (
    <div className="space-y-6">
      <header>
        <h2 className="text-lg font-semibold text-[var(--color-text)]">{t.pageTitle}</h2>
        <p className="mt-1 text-sm text-[var(--color-text-muted)]">{t.pageDescription}</p>
      </header>

      {subs.isLoading ? (
        <div className="text-sm text-[var(--color-text-muted)]">{t.real.loading}</div>
      ) : subs.isError ? (
        <div className="text-sm text-rose-700">{t.real.loadError}</div>
      ) : (
        <DataTableShell>
          <table>
            <thead>
              <tr>
                <th>{t.columns.category}</th>
                <th className="w-24 text-center">
                  <span className="inline-flex items-center gap-1.5"><Mail className="h-3.5 w-3.5 text-[var(--color-text-muted)]" />{t.channels.email}</span>
                </th>
                <th className="w-24 text-center">
                  <span className="inline-flex items-center gap-1.5"><Bell className="h-3.5 w-3.5 text-[var(--color-text-muted)]" />{t.channels['in-app']}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {categories.map((cat) => {
                const labels = t.categories[cat.id]
                const cur = stateFor(cat)
                return (
                  <tr key={cat.id}>
                    <td className="py-3">
                      <p className="text-sm font-medium text-[var(--color-text)]">{labels.label}</p>
                      <p className="mt-0.5 text-xs text-[var(--color-text-muted)]">{labels.description}</p>
                    </td>
                    <td className="py-3 text-center">
                      <input type="checkbox" checked={cur.email} disabled={upsert.isPending}
                        onChange={() => upsert.mutate({ eventType: cat.id, email: !cur.email, inApp: cur.inApp })}
                        className="h-4 w-4 cursor-pointer rounded border-[var(--color-border-strong)]" />
                    </td>
                    <td className="py-3 text-center">
                      <input type="checkbox" checked={cur.inApp} disabled={upsert.isPending}
                        onChange={() => upsert.mutate({ eventType: cat.id, email: cur.email, inApp: !cur.inApp })}
                        className="h-4 w-4 cursor-pointer rounded border-[var(--color-border-strong)]" />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </DataTableShell>
      )}

      <p className="text-xs text-[var(--color-text-muted)]">{t.footerNote}</p>
    </div>
  )
}

export function Notifications({ surface }: { surface: SettingsSurface }) {
  // Branch BEFORE any hook runs — the maket body lives in its own component so
  // hook order never depends on USE_REAL_API.
  if (USE_REAL_API) return <RealNotifications surface={surface} />
  return <MaketNotifications surface={surface} />
}

function MaketNotifications({ surface }: { surface: SettingsSurface }) {
  const push = useToastStore((s) => s.push)
  const t = useT(notificationsI18n)
  const categories = SURFACE_CATEGORY_CONFIG[surface]
  const [state, setState] = useState<Record<string, Record<Channel, boolean>>>(
    () =>
      categories.reduce<Record<string, Record<Channel, boolean>>>((acc, c) => {
        acc[c.id] = { ...c.defaults }
        return acc
      }, {}),
  )

  const toggle = (categoryId: CategoryKey, channel: Channel) => {
    setState((s) => ({
      ...s,
      [categoryId]: { ...s[categoryId], [channel]: !s[categoryId][channel] },
    }))
    push({
      variant: 'info',
      title: t.toastTitle,
      description: t.toastDescription,
      duration_ms: 3000,
    })
  }

  return (
    <div className="space-y-6">
      <header>
        <h2 className="text-lg font-semibold text-[var(--color-text)]">{t.pageTitle}</h2>
        <p className="mt-1 text-sm text-[var(--color-text-muted)]">{t.pageDescription}</p>
      </header>

      <DataTableShell>
        <table>
          <thead>
            <tr>
              <th>{t.columns.category}</th>
              {CHANNEL_ORDER.map((c) => {
                const Icon = CHANNEL_ICONS[c]
                return (
                  <th
                    key={c}
                    className="w-24 text-center"
                  >
                    <span className="inline-flex items-center gap-1.5">
                      <Icon className="h-3.5 w-3.5 text-[var(--color-text-muted)]" />
                      {t.channels[c]}
                    </span>
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {categories.map((cat) => {
              const labels = t.categories[cat.id]
              return (
                <tr key={cat.id}>
                  <td className="py-3">
                    <p className="text-sm font-medium text-[var(--color-text)]">
                      {labels.label}
                    </p>
                    <p className="mt-0.5 text-xs text-[var(--color-text-muted)]">
                      {labels.description}
                    </p>
                  </td>
                  {CHANNEL_ORDER.map((c) => (
                    <td key={c} className="py-3 text-center">
                      <label className="inline-flex cursor-pointer items-center">
                        <input
                          type="checkbox"
                          checked={state[cat.id][c]}
                          onChange={() => toggle(cat.id, c)}
                          className="h-4 w-4 cursor-pointer rounded border-[var(--color-border-strong)]"
                        />
                      </label>
                    </td>
                  ))}
                </tr>
              )
            })}
          </tbody>
        </table>
      </DataTableShell>

      <p className="text-xs text-[var(--color-text-muted)]">{t.footerNote}</p>
    </div>
  )
}
